<!--
  @fileoverview Singpass Authentication Component

  Renders a button to initiate Singpass (Singapore government identity) authentication.
  Opens the Singpass authentication URL in a new window.

  @component MessageSingPass
  @displayName Singpass Login Button

  Features:
  - Opens Singpass QR code authentication
  - Configurable client ID and redirect URI
  - State parameter includes sender ID, recipient, and app source

  @example
  <MessageSingPass
    :button-outline="'primary'"
  />

  @vue3migration
  - Converted to script setup pattern
  - Replaced this.$store.state with Pinia useMainStore()
-->
<template>
  <el-row type="flex" class="mx-4 mb-4 mt-0 flex-row flex-wrap">
    <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
    <el-button
      target="_blank"
      :type="buttonOutline"
      :class="{ 'blue-button': buttonOutline === 'primary' }"
      @click="openSingpassAuthUrl"
    >
      Open QR Code
    </el-button>
  </el-row>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/Singpass
 * @description Singpass authentication button for Singapore identity verification
 */
import { useMainStore } from "@/stores/main";

/**
 * Component props
 */
defineProps({
  buttonOutline: {
    type: String,
    default: "",
  },
});

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Open Singpass authentication URL in new window
 *
 * Constructs the OAuth 2.0 authorization URL with:
 * - scope: openid (for OIDC)
 * - response_type: code (authorization code flow)
 * - state: senderId_recipient_appSource (for CSRF protection and context)
 * - nonce: timestamp (for replay attack prevention)
 * - client_id: from settings
 * - redirect_uri: from settings
 */
function openSingpassAuthUrl() {
  const baseUrl = store.settings.singpassAuthURL;
  const senderId = store.uid;
  const recipient = location.hostname;
  const appSource = store.settings.source;
  const singpassState = `${senderId}_${recipient}_${appSource}`;
  const singpassNonce = Date.now();
  const singpassClientId = store.settings.singpassClientId;
  const singpassRedirectUri = store.settings.singpassRedirectURI;

  const authURL = `${baseUrl}?scope=openid&response_type=code&state=${singpassState}&nonce=${singpassNonce}&client_id=${singpassClientId}&redirect_uri=${singpassRedirectUri}`;

  window.open(authURL);
}
</script>

<style lang="scss" scoped>
.el-button {
  margin: 0 5px 5px 0;
  // flex: 1 1 48%;
  border-radius: 25px;
  font-family: var(--keyreply-font-family);
  &.active {
    background: transparent;
  }
}
</style>

<style lang="scss" scoped>
.ui-beta {
  .el-button {
    margin: 0 5px 5px 0;
    border-radius: 8px;
    white-space: normal;
    text-align: left;

    &.blue-button {
      color: #4e6cce;
      background: #dce2f5;
      border-color: #4e6cce;

      &:hover {
        background: #4e6cce;
        color: #fff;
      }
    }

    &.active {
      background: transparent;
    }
  }
}
</style>
